import { useState, useCallback, useRef, useEffect } from 'react';

const defaultFormData = {
  patente: '',
  rut_contacto: '',
  contacto: '',
  email: '',
  telefono: '',
  odometro: '',
  emisora_radio: '',
  tipo_trabajo_ot: '',
  solicitud_cliente: '',
  nivel_estanque: 0,
  sin_inventario: false,
  sin_danos: false,
  sucio: false,
  inventario: {
    plumillas: null,
    radio: null,
    panel_radio: null,
    herramientas: null,
    gata: null,
    extintor: null,
    botiquin: null,
    permiso_circulacion: null,
    libro_garantia: null,
    encendedor: null,
    accesorios: null,
    neumatico_repuesto: null,
    tapa_rueda: null,
    cubre_pisos: null,
    observaciones: '',
  },
  danos: [],
  danos_img: '',
  firma_cliente: '',
  firma_recepcionista: '',
  nombre_recepcionista: '',
  imagenes: [],
  quien_retira: '',
};

/**
 * Maps API response to formData shape, handling patent→patente normalization.
 */
function mapRecepcionToFormData(recepcion) {
  if (!recepcion) return { ...defaultFormData };

  return {
    patente: recepcion.patente || recepcion.patent || '',
    rut_contacto: recepcion.rut_contacto || '',
    contacto: recepcion.contacto || '',
    email: recepcion.correocontacto || '',
    telefono: recepcion.fonocontacto || '',
    odometro: recepcion.odometro || '',
    emisora_radio: recepcion.emisora_radio || '',
    tipo_trabajo_ot: recepcion.tipo_trabajo_ot || '',
    solicitud_cliente: recepcion.reparacion_requerida || '',
    nivel_estanque: recepcion.nivel_estanque ?? 0,
    sin_inventario: recepcion.sin_inventario || false,
    sin_danos: recepcion.sin_danos || false,
    sucio: recepcion.sucio || false,
    inventario: {
      plumillas: recepcion.inventario?.plumillas ?? false,
      radio: recepcion.inventario?.radio ?? false,
      panel_radio: recepcion.inventario?.panel_radio ?? false,
      herramientas: recepcion.inventario?.herramientas ?? false,
      gata: recepcion.inventario?.gata ?? false,
      extintor: recepcion.inventario?.extintor ?? false,
      botiquin: recepcion.inventario?.botiquin ?? false,
      permiso_circulacion: recepcion.inventario?.permiso_circulacion ?? false,
      libro_garantia: recepcion.inventario?.libro_garantia ?? false,
      encendedor: recepcion.inventario?.encendedor ?? false,
      accesorios: recepcion.inventario?.accesorios ?? false,
      neumatico_repuesto: recepcion.inventario?.neumatico_repuesto ?? false,
      tapa_rueda: recepcion.inventario?.tapa_rueda ?? false,
      cubre_pisos: recepcion.inventario?.cubre_pisos ?? false,
      observaciones: recepcion.inventario?.observaciones || '',
    },
    danos: recepcion.danos || [],
    danos_img: recepcion.danos_img || '',
    firma_cliente: recepcion.firma_cliente || '',
    firma_recepcionista: recepcion.firma_recepcionista || '',
    nombre_recepcionista: recepcion.nombre_recepcionista || '',
    imagenes: recepcion.imagenes || [],
    quien_retira: recepcion.quien_retira || '',
  };
}

/**
 * Validates email format (simplified RFC 5322).
 */
function isValidEmail(email) {
  if (!email) return true; // email is not required
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validates phone format (numeric, 7-15 digits).
 */
function isValidPhone(phone) {
  if (!phone) return true; // phone is not required
  const phoneRegex = /^\d{7,15}$/;
  return phoneRegex.test(phone);
}

/**
 * Custom hook for vehicle reception edit form.
 * Manages form state, validation, change handlers, and dirty detection.
 *
 * @param {Object} recepcion - The reception object from API (useRecepcionVehiculoPorId)
 * @returns {Object} Form state and handlers
 */
const useReceptionEditForm = (recepcion) => {
  const initialFormData = mapRecepcionToFormData(recepcion);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // Keep a ref to initial data for comparison
  const initialRef = useRef(initialFormData);

  // Sync formData when recepcion changes (async data arrival)
  useEffect(() => {
    if (recepcion) {
      setFormData(mapRecepcionToFormData(recepcion));
    }
  }, [recepcion]);

  const handleChange = useCallback((e) => {
    if (!e?.target) return;
    const { name, value } = e.target;

    setFormData((prev) => {
      const newValue = name === 'patente' ? value.trim().toUpperCase() : value;
      return { ...prev, [name]: newValue };
    });
    setIsDirty(true);
  }, []);

  const handleInventoryChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      inventario: {
        ...prev.inventario,
        [field]: value,
      },
    }));
    setIsDirty(true);
  }, []);

  const handleDamagesChange = useCallback((base64OrArray) => {
    setFormData((prev) => {
      if (typeof base64OrArray === 'string') {
        return { ...prev, danos_img: base64OrArray };
      }
      return { ...prev, danos: base64OrArray };
    });
    setIsDirty(true);
  }, []);

  const handleFuelChange = useCallback((value) => {
    setFormData((prev) => ({
      ...prev,
      nivel_estanque: value,
    }));
    setIsDirty(true);
  }, []);

  const handleSignatureChange = useCallback((field, data) => {
    setFormData((prev) => ({
      ...prev,
      [field]: data,
    }));
    setIsDirty(true);
  }, []);

  const handleImagesChange = useCallback((images) => {
    setFormData((prev) => ({
      ...prev,
      imagenes: images,
    }));
    setIsDirty(true);
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    // Required fields
    if (!formData.patente || !formData.patente.trim()) {
      newErrors.patente = 'Patente es requerido';
    }

    if (!formData.contacto || !formData.contacto.trim()) {
      newErrors.contacto = 'Contacto es requerido';
    }

    // Format validation
    if (formData.email && !isValidEmail(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.telefono && !isValidPhone(formData.telefono)) {
      newErrors.telefono = 'Teléfono debe ser numérico';
    }

    if (!formData.sin_inventario) {
      const inventoryFields = [
        'plumillas', 'radio', 'panel_radio', 'herramientas',
        'gata', 'extintor', 'botiquin', 'permiso_circulacion',
        'libro_garantia', 'encendedor', 'accesorios',
        'neumatico_repuesto', 'tapa_rueda', 'cubre_pisos',
      ];
      const hasNull = inventoryFields.some(
        (field) => formData.inventario[field] === null
      );
      if (hasNull) {
        newErrors.inventario = 'Debe marcar SÍ o NO para todos los ítems del inventario';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  return {
    formData,
    errors,
    handleChange,
    handleInventoryChange,
    handleDamagesChange,
    handleFuelChange,
    handleSignatureChange,
    handleImagesChange,
    validate,
    isDirty,
  };
};

export default useReceptionEditForm;
